import { memo, ReactNode, useMemo } from 'react';

import styled from 'styled-components';

import { IconButton } from '@components/icon-button';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconCopyContent } from '@components/icons/copy-content';
import { Text } from '@components/text';
import { useClipboard } from '@hooks/use-clipboard';
import { getScopedTranslate, useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { formatNumber } from '@utils/numbers';

export interface CodeBlockProps {
  header?: string | ReactNode;
  headerAction?: ReactNode;
  data: object;
  hideFooter?: boolean;
  maxHeight?: string;
}

// Strings and keys above this are truncated. The highlighter regex backtracks
// per character within one quoted token and overflows the stack at ~4.2M chars,
// so this sits ~40x inside that floor — high enough to still show a stack trace
// or an upstream error page in full.
const MAX_VALUE_LENGTH = 100_000;

// How much of a truncated value survives, so the reader can still tell what it
// was ("data:application/pdf;base64,JVBERi0x...").
const VALUE_PREVIEW_LENGTH = 200;

// Above this, skip highlighting. Not a crash guard — keys and values are both
// capped, so no single token can reach the overflow — but the block builds one
// <div> per line, so something has to bound the DOM.
const MAX_HIGHLIGHT_LENGTH = 1_000_000;

function truncate(value: string, placeholder: (length: number) => string) {
  return value.length > MAX_VALUE_LENGTH
    ? value.slice(0, VALUE_PREVIEW_LENGTH) + placeholder(value.length)
    : value;
}

function replaceOversizedValues(value: unknown, placeholder: (length: number) => string): unknown {
  if (typeof value === 'string') {
    return truncate(value, placeholder);
  }
  if (Array.isArray(value)) {
    return value.map((item) => replaceOversizedValues(item, placeholder));
  }
  if (value !== null && typeof value === 'object') {
    // Keys are capped too: they reach the same regex and nothing else bounds them.
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        truncate(key, placeholder),
        replaceOversizedValues(item, placeholder),
      ]),
    );
  }
  return value;
}

function escapeHtml(json: string) {
  return json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function syntaxHighlight(json: string) {
  const escaped = escapeHtml(json);
  if (escaped.length > MAX_HIGHLIGHT_LENGTH) {
    return escaped;
  }
  return escaped.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    function (match) {
      var cls = 'number';
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = 'key';
        } else {
          cls = 'string';
        }
      } else if (/true|false/.test(match)) {
        cls = 'boolean';
      } else if (/null/.test(match)) {
        cls = 'null';
      }
      return '<span class="' + cls + '">' + match + '</span>';
    },
  );
}

export const CodeBlock = memo(function CodeBlock({
  header,
  headerAction,
  data,
  hideFooter,
  maxHeight = pxToRem(384),
}: CodeBlockProps) {
  // `lang`, not `t` — useScopedTranslate rebuilds `t` on every render, so a dep
  // on it would defeat the memo and re-walk the whole payload each time.
  const { lang, t } = useScopedTranslate(locale);
  const { copied, onCopy } = useClipboard();
  const { codeLines, hasTruncated } = useMemo(() => {
    const scoped = getScopedTranslate(locale, undefined, lang);
    let truncated = false;
    const displayedData = replaceOversizedValues(data, (length) => {
      truncated = true;
      return scoped.t('tooLarge', { length: formatNumber(length) });
    });
    const lines = syntaxHighlight(JSON.stringify(displayedData, null, 2))
      .split('\n')
      .map((line, index) => (
        <div key={index}>
          <span className="line-number">{index + 1}</span>
          <span dangerouslySetInnerHTML={{ __html: line }}></span>
        </div>
      ));
    return { codeLines: lines, hasTruncated: truncated };
  }, [data, lang]);
  const hideHeader = !header && !headerAction;
  // Once something is truncated, copy is the only route to the full value, so
  // the control has to appear even where the consumer asked for no footer.
  const showFooter = !hideFooter || hasTruncated;

  return (
    <CodeContainer>
      {!hideHeader && (
        <HeaderContainer>
          {typeof header === 'string' ? (
            <Text font="bold14" text={header} color="textSecondary" />
          ) : (
            (header ?? <span />)
          )}
          {headerAction}
        </HeaderContainer>
      )}
      <StyledCodeBlock $hideHeader={hideHeader} $hideFooter={!showFooter} $maxHeight={maxHeight}>
        {codeLines}
      </StyledCodeBlock>
      {showFooter && (
        <HeaderFooterContainer>
          <IconButton
            variant="ghost-purple"
            tooltip={t('copy')}
            icon={copied ? <IconCheckmark /> : <IconCopyContent />}
            onClick={() => onCopy(JSON.stringify(data, null, 2))}
          />
        </HeaderFooterContainer>
      )}
    </CodeContainer>
  );
});

const CodeContainer = styled.div({
  border: `1px solid ${theme.color.border20}`,
  borderRadius: theme.space[2],
  backgroundColor: theme.color.uiTrans5,
});

const HeaderFooterContainer = styled.div({
  padding: `${theme.space[2]} ${theme.space[3]}`,
});

const HeaderContainer = styled(HeaderFooterContainer)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.space[4],
});

const StyledCodeBlock = styled.pre<{
  $hideHeader?: boolean;
  $hideFooter?: boolean;
  $maxHeight?: string;
}>(({ $hideHeader = false, $hideFooter = false, $maxHeight = pxToRem(384) }) => ({
  fontSize: pxToRem(14),
  overflow: 'auto',
  maxHeight: $maxHeight,
  margin: 0,
  flexGrow: 1,
  ...(!$hideHeader && {
    borderTop: `1px solid ${theme.color.border20}`,
  }),
  ...(!$hideFooter && {
    borderBottom: `1px solid ${theme.color.border20}`,
  }),
  padding: `${theme.space[2]} ${theme.space[3]}`,
  'word-wrap': 'break-word',
  'white-space': 'pre',
  color: theme.color.textPrimary,
  '.line-number': {
    color: theme.color.elementPurple50,
    display: 'inline-block',
    width: theme.space[10],
    textAlign: 'right',
    paddingRight: theme.space[1],
    opacity: 0.5,
  },
  '.string': {
    color: theme.color.elementGreen100,
  },
  '.boolean': {
    color: theme.color.elementIndigo100,
  },
  '.number': {
    color: theme.color.elementOrange100,
  },
  '.null': {
    color: theme.color.elementPurple80,
  },
  '.key': {
    color: theme.color.elementBlue100,
  },
}));

const locale = {
  en: {
    copy: 'Copy',
    tooLarge:
      '... << truncated — {length} characters total, use the copy button for the full value >>',
  },
};
